import {
  getLeoUuid,
  LeoUUID,
  getInt32,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { PivotTableRow, PivotTableRowDTO } from "./pivotTableRow";
/* eslint-disable import/export */
export abstract class GetPivotTableDataRPC implements
  LeoRPC<GetPivotTableDataRPC.Request, GetPivotTableDataRPC.Response, GetPivotTableDataRPC.Errors.Errors>
{
  abstract execute(
    request: GetPivotTableDataRPC.Request
  ): Promise<LeoRPCResult<GetPivotTableDataRPC.Response, GetPivotTableDataRPC.Errors.Errors>>;
}

export namespace GetPivotTableDataRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    columnIndex: number;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPivotTableDataRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly columnIndex: number;
  
    static readonly constraints = {
      "columnIndex": {
        "minValue": 0,
        "maxValue": 199,
      }
    }
  
    constructor(
    projectId: LeoUUID, 
    columnIndex: number
    ) {
      this.projectId = projectId;
      Request.validateColumnIndex(columnIndex);
      this.columnIndex = columnIndex;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const columnIndex = getInt32(dto, "columnIndex", InvalidRequestColumnIndexError);
      return new Request(
        projectId,
        columnIndex
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          columnIndex: this.columnIndex
      };
      return dto;
    }
    private static validateColumnIndex = (columnIndex: number): void => {
      if(columnIndex < 0) {
        throw new InvalidRequestColumnIndexMinValueError(
          0,
          `Attribute columnIndex has value ${columnIndex}. Min value is 0.`
        );
      }
      if(columnIndex > 199) {
        throw new InvalidRequestColumnIndexMaxValueError(
          199,
          `Attribute columnIndex has value ${columnIndex}. Max value is 199.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetPivotTableDataRPC(projectId='${this.projectId}',columnIndex='${this.columnIndex}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    tableData: PivotTableRowDTO[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPivotTableDataResponse: boolean = true;
  
    readonly tableData: PivotTableRow[];
  
  
    constructor(
    tableData: PivotTableRow[]
    ) {
      this.tableData = tableData;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const tableData = getList<PivotTableRowDTO, PivotTableRow>(
        dto,
        "tableData",
        PivotTableRow.fromDTO,
        InvalidResponseTableDataError
      );
      return new Response(
        tableData
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          tableData: this.tableData.map((e: PivotTableRow) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPivotTableData(tableData='${this.tableData}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ColumnNotFound = "COLUMN_NOT_FOUND"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetPivotTableDataRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ColumnNotFoundDTO {
      code: string;
    }
    
    export class ColumnNotFound extends LeoRPCError<RPCError> {
      readonly isColumnNotFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ColumnNotFound;
        super(code);
      }
    
    
      toDTO(): ColumnNotFoundDTO {
        const dto: ColumnNotFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ColumnNotFound.prototype.toString = function toString(): string {
      return `GetPivotTableDataRPC.COLUMN_NOT_FOUND()`;
    };
    

    export type Errors = InvalidProjectId | ColumnNotFound;
    export type ErrorsDTO = InvalidProjectIdDTO | ColumnNotFoundDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ColumnNotFound:
          return new ColumnNotFound();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetPivotTableDataRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestColumnIndexError extends GetPivotTableDataRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestColumnIndexError";
  }
}

export class InvalidRequestColumnIndexMaxValueError extends InvalidRequestColumnIndexError {
  readonly maxValue: number;
  constructor(maxValue: number, message?: string) {
    super(message);
    this.maxValue = maxValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestColumnIndexMaxValueError";
  }
}

export class InvalidRequestColumnIndexMinValueError extends InvalidRequestColumnIndexError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestColumnIndexMinValueError";
  }
}

export class InvalidResponseTableDataError extends GetPivotTableDataRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTableDataError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetPivotTableDataRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseColumnNotFoundError extends GetPivotTableDataRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseColumnNotFoundError";
  }
}

